/* prettier-ignore-file */
import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Modal,
  Image,
} from "react-native";
import { useNavigate } from "react-router-dom";
import SignaturePad from "./SignaturePad";
import SignatureCaptureModal from "./SignatureCaptureModal";
import GoogleAddressInput from "./GoogleAddressInput";
import jsPDF from "jspdf";
import { storage, db } from "../firebase.config";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { getSettingsPath } from "../utils/dbPaths";

const RentalWaiver = ({ 
  customerName, 
  onWaiverSigned, 
  onSaveWaiverOnly,
  onCancel, 
  legalStoreName = "Legal Store Name", 
  legalStoreAddress = "", 
  cancellationPolicyProp = "",
  hasExistingPayment = false,
  isOriginalBooker = false,
  bookingId = null,
  paymentUrl = null,
  waiverSaved = false,
  initialWaiverData = null,
  onFormChange = null,
}) => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typedName, setTypedName] = useState("");
  const [homeAddress, setHomeAddress] = useState("");
  const [fullSignature, setFullSignature] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  // Phone breakpoint; the inline pad is fine with a mouse but cramped with a finger.
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isPhone = viewportWidth < 768;
  const initialDataAppliedRef = useRef(false);
  const [agreed, setAgreed] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [savingWaiver, setSavingWaiver] = useState(false);
  const [addressFieldClicked, setAddressFieldClicked] = useState(false);
  const [addressSelectedFromDropdown, setAddressSelectedFromDropdown] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const prevWaiverSavedRef = useRef(waiverSaved);
  const navigate = useNavigate();

  // Show success modal only when waiver is saved in current session (transitions from false to true)
  useEffect(() => {
    // Only show modal if waiverSaved transitions from false to true (not if it's already true on mount)
    if (waiverSaved && !prevWaiverSavedRef.current) {
      setShowSuccessModal(true);
    }
    prevWaiverSavedRef.current = waiverSaved;
  }, [waiverSaved]);

  // Load waiver template from Firestore
  useEffect(() => {
    const loadTemplate = async () => {
      try {
        console.log("📋 Loading waiver template from Firestore...");
        const settingsPath = getSettingsPath();
        const templateRef = doc(db, settingsPath, "waiverTemplate");
        const templateDoc = await getDoc(templateRef);
        
        let templateData;
        
        if (!templateDoc.exists()) {
          console.log("⚠️ No waiver template found, creating default template...");
          
          // Create default waiver template
          const defaultTemplate = {
            sections: [
              {
                id: "assumption-of-risk",
                title: "ASSUMPTION OF RISK",
                content: "I understand that renting and using equipment from <storeName> involves inherent risks including, but not limited to, accidents, injuries, property damage, or equipment malfunction. I voluntarily assume all such risks associated with my use of the rented equipment.",
                requiresInitial: true,
                order: 1,
                visible: true
              },
              {
                id: "liability-waiver",
                title: "RELEASE OF LIABILITY",
                content: "In consideration of being permitted to rent equipment from <storeName>, I hereby release, waive, and discharge <storeName>, its owners, employees, agents, and representatives from any and all liability, claims, demands, or causes of action arising from personal injury, property damage, or any other loss sustained while using the rented equipment, whether caused by negligence or otherwise.",
                requiresInitial: true,
                order: 2,
                visible: true
              },
              {
                id: "equipment-responsibility",
                title: "EQUIPMENT CARE AND RESPONSIBILITY",
                content: "I acknowledge that I am responsible for the rented equipment from the time of pickup until its return to <storeName>. I agree to use the equipment in a safe and responsible manner and will be liable for any damage, loss, or theft of the equipment during the rental period. I will return the equipment in the same condition as received, normal wear and tear excepted.",
                requiresInitial: true,
                order: 3,
                visible: true
              },
              {
                id: "indemnification",
                title: "INDEMNIFICATION",
                content: "I agree to indemnify, defend, and hold harmless <storeName> from and against any and all claims, liabilities, damages, losses, or expenses (including attorney's fees) arising out of or resulting from my use of the rented equipment or any actions taken by me during the rental period.",
                requiresInitial: false,
                order: 4,
                visible: true
              }
            ],
            finalSignatureSection: {
              title: "FULL SIGNATURE",
              content: "By signing below, I acknowledge that I have read, understood, and agree to all terms and conditions contained in this Liability Waiver and Release. I understand that I am giving up substantial rights, including my right to sue. I confirm that I am at least 18 years of age and am legally competent to sign this agreement.",
              requiresTypedName: true,
              requiresHomeAddress: true,
              requiresSignature: true
            },
            lastUpdated: new Date().toISOString()
          };
          
          // Save default template to Firestore
          await setDoc(templateRef, defaultTemplate);
          console.log("✅ Default waiver template created");
          templateData = defaultTemplate;
        } else {
          templateData = templateDoc.data();
          console.log("✅ Waiver template loaded:", templateData);
        }
        
        setTemplate(templateData);
        
      } catch (error) {
        console.error("❌ Error loading waiver template:", error);
        alert("Error: Could not load waiver template. " + error.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadTemplate();
  }, []);

  // Pre-fill form from store when user returns (e.g. back from Stripe)
  useEffect(() => {
    if (!template || !initialWaiverData || initialDataAppliedRef.current) return;
    initialDataAppliedRef.current = true;
    if (initialWaiverData.typedName) setTypedName(initialWaiverData.typedName);
    if (initialWaiverData.homeAddress) setHomeAddress(initialWaiverData.homeAddress);
    if (initialWaiverData.fullSignature) setFullSignature(initialWaiverData.fullSignature);
  }, [template, initialWaiverData]);

  // Replace variables in text
  const replaceVariables = (text) => {
    if (!text) return "";
    return text
      .replace(/<storeName>/g, legalStoreName)
      .replace(/<storeAddress>/g, legalStoreAddress)
      .replace(/<customerName>/g, customerName)
      .replace(/<date>/g, new Date().toLocaleDateString());
  };

  const generateWaiverPDF = async () => {
    try {
      console.log("📄 Generating Waiver PDF from template...");
      
      const pdfDoc = new jsPDF();
      const pageWidth = pdfDoc.internal.pageSize.getWidth();
      const margin = 15;
      let yPos = 20;

      // Title
      pdfDoc.setFontSize(16);
      pdfDoc.setFont(undefined, "bold");
      pdfDoc.text("LIABILITY WAIVER AND RELEASE", pageWidth / 2, yPos, {
        align: "center",
      });
      yPos += 7;

      pdfDoc.setFontSize(12);
      pdfDoc.text(
        `${legalStoreName} - Equipment Rental Agreement`,
        pageWidth / 2,
        yPos,
        { align: "center" }
      );
      yPos += 7;
      
      if (legalStoreAddress && legalStoreAddress.trim()) {
        pdfDoc.setFontSize(10);
        pdfDoc.setFont(undefined, "normal");
        pdfDoc.text(legalStoreAddress, pageWidth / 2, yPos, { align: "center" });
        yPos += 10;
      } else {
        yPos += 5;
      }

      // Participant Information
      pdfDoc.setFontSize(11);
      pdfDoc.setFont(undefined, "bold");
      pdfDoc.text("PARTICIPANT INFORMATION", margin, yPos);
      yPos += 7;

      pdfDoc.setFont(undefined, "normal");
      pdfDoc.text(`Participant Name: ${customerName}`, margin, yPos);
      yPos += 7;
      pdfDoc.text(`Date: ${new Date().toLocaleDateString()}`, margin, yPos);
      yPos += 7;
      pdfDoc.text(`Home Address: ${homeAddress || "Not provided"}`, margin, yPos);
      yPos += 12;

      // Render sections from template
      const visibleSections = template.sections.filter(s => s.visible);
      
      for (let i = 0; i < visibleSections.length; i++) {
        const section = visibleSections[i];
        const sectionContent = replaceVariables(section.content);
        
        // Check if we need a new page
        if (yPos > 250) {
          pdfDoc.addPage();
          yPos = 20;
        }

        pdfDoc.setFont(undefined, "bold");
        pdfDoc.text(`${section.order}. ${section.title}`, margin, yPos);
        yPos += 7;
        
        pdfDoc.setFont(undefined, "normal");
        const contentLines = pdfDoc.splitTextToSize(sectionContent, pageWidth - 2 * margin);
        pdfDoc.text(contentLines, margin, yPos);
        yPos += contentLines.length * 5 + 5;

      }

      // Final signature section
      if (yPos > 230) {
        pdfDoc.addPage();
        yPos = 20;
      }

      pdfDoc.setFont(undefined, "bold");
      pdfDoc.text(template.finalSignatureSection.title, margin, yPos);
      yPos += 7;
      
      pdfDoc.setFont(undefined, "normal");
      const finalContent = replaceVariables(template.finalSignatureSection.content);
      const finalLines = pdfDoc.splitTextToSize(finalContent, pageWidth - 2 * margin);
      pdfDoc.text(finalLines, margin, yPos);
      yPos += finalLines.length * 5 + 10;

      if (template.finalSignatureSection.requiresTypedName) {
        pdfDoc.text(`Typed Full Name: ${typedName}`, margin, yPos);
        yPos += 10;
      }

      if (template.finalSignatureSection.requiresSignature && fullSignature) {
        pdfDoc.text("Signature:", margin, yPos);
        yPos += 5;
        pdfDoc.addImage(fullSignature, "PNG", margin, yPos, 60, 20);
        yPos += 25;
      }

      pdfDoc.text(`Date: ${new Date().toLocaleDateString()}`, margin, yPos);

      const pdfBlob = pdfDoc.output("blob");
      return pdfBlob;
    } catch (error) {
      console.error("Error generating PDF:", error);
      throw error;
    }
  };

  // Generate waiver data (shared helper)
  const generateWaiverData = async () => {

    // Validate final section requirements
    if (template.finalSignatureSection.requiresTypedName && !typedName.trim()) {
      alert("Please type your full name");
      return null;
    }

    if (template.finalSignatureSection.requiresHomeAddress && !homeAddress.trim()) {
      alert("Please enter your home address");
      return null;
    }

    if (template.finalSignatureSection.requiresSignature && !fullSignature) {
      alert("Please provide your signature");
      return null;
    }

    if (!agreed) {
      alert("Please accept the agreement");
      return null;
    }

    console.log("✅ Validation passed, starting PDF generation...");

    // Generate PDF
    console.log("Generating waiver PDF...");
    const pdfBlob = await generateWaiverPDF();
    console.log("PDF generated successfully, size:", pdfBlob.size);

    // Upload to Firebase Storage
    const timestamp = Date.now();
    const sanitizedName = typedName.trim().replace(/[^a-zA-Z0-9]/g, "_");
    const fileName = `waivers/${sanitizedName}_${timestamp}.pdf`;
    const storageRef = ref(storage, fileName);

    console.log("Uploading waiver PDF to Firebase Storage...", fileName);
    await uploadBytes(storageRef, pdfBlob);
    console.log("Upload complete!");

    // Get download URL
    const pdfUrl = await getDownloadURL(storageRef);
    console.log("Waiver PDF uploaded successfully:", pdfUrl);

    const waiverData = {
      typedName: typedName.trim(),
      homeAddress: homeAddress.trim(),
      fullSignature,
      signedAt: new Date().toISOString(),
      customerName,
      pdfUrl,
      pdfFileName: fileName,
      waiverType: "template-based",
      templateVersion: template.lastUpdated || new Date().toISOString(),
    };

    return waiverData;
  };

  // Handler for saving waiver only
  const handleSaveWaiver = async () => {
    console.log("💾 Save Waiver Only button clicked!");
    
    try {
      setSavingWaiver(true);
      const waiverData = await generateWaiverData();
      
      if (!waiverData) {
        setSavingWaiver(false);
        return;
      }

      console.log("✅ Waiver generated, calling onSaveWaiverOnly...");
      await onSaveWaiverOnly?.(waiverData);
    } catch (error) {
      console.error("Error saving waiver:", error);
      alert("Error: Failed to save waiver. Please try again.");
    } finally {
      setSavingWaiver(false);
    }
  };

  // Handle Finish button click - navigate to view booking page
  const handleFinish = () => {
    if (bookingId) {
      navigate(`/view-booking/${bookingId}`);
    } else {
      onCancel?.();
    }
  };

  // Handler for continuing to payment
  const handleSignWaiver = async () => {
    console.log("🔵 handleSignWaiver called!");
    
    try {
      setGenerating(true);
      const waiverData = await generateWaiverData();
      
      if (!waiverData) {
        setGenerating(false);
        return;
      }

      console.log("✅ Waiver signed, proceeding to payment with data:", waiverData);
      onWaiverSigned?.(waiverData);
    } catch (error) {
      console.error("Error processing waiver:", error);
      alert("Error: Failed to process waiver. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  // Check if can sign
  const canSign = () => {
    
    const nameValid = !template?.finalSignatureSection?.requiresTypedName || typedName.trim();
    // Address must have minimum 7 characters
    const addressValid = !template?.finalSignatureSection?.requiresHomeAddress || 
      (homeAddress.trim().length >= 7);
    const signatureValid = !template?.finalSignatureSection?.requiresSignature || fullSignature;
    
    return nameValid && addressValid && signatureValid && agreed;
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#0E7C6B" />
        <Text style={styles.loadingText}>Loading waiver...</Text>
      </View>
    );
  }

  if (!template) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>❌ Waiver template not configured</Text>
        <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.cancelButton} onPress={onCancel}>
          <Text style={styles.cancelButtonText}>Go Back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const visibleSections = template.sections.filter(s => s.visible);

  return (
    <ScrollView style={styles.container}>
      <Text style={styles.title}>LIABILITY WAIVER AND RELEASE</Text>
      <Text style={styles.subtitle}>
        {legalStoreName} - Equipment Rental Agreement
      </Text>
      {legalStoreAddress && (
        <Text style={[styles.subtitle, { fontSize: 12, marginTop: 4, color: "#5C6B68" }]}>
          {legalStoreAddress}
        </Text>
      )}

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>PARTICIPANT INFORMATION</Text>
        <Text style={styles.text}>
          Participant Name: {customerName || "[Name will be filled automatically]"}
        </Text>
        <Text style={styles.text}>Date: {new Date().toLocaleDateString()}</Text>
      </View>

      {/* Dynamic sections from template */}
      {visibleSections.map((section) => (
        <View key={section.id} style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionNumber}>{section.order}</Text>
            <Text style={styles.sectionTitle}>{section.title}</Text>
          </View>
          <Text style={styles.text}>
            {replaceVariables(section.content)}
          </Text>
        </View>
      ))}

      {/* Final Signature Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{template.finalSignatureSection.title}</Text>
        <Text style={styles.text}>
          {replaceVariables(template.finalSignatureSection.content)}
        </Text>

        {template.finalSignatureSection.requiresTypedName && (
          <View style={styles.nameInputContainer}>
            <Text style={styles.nameInputLabel}>
              Type Your Full Name <Text style={styles.requiredAsterisk}>*</Text>
            </Text>
            <TextInput
              style={[
                styles.nameInput,
                !typedName || typedName.trim() === "" ? styles.nameInputRequired : null
              ]}
              placeholder="Enter your full legal name"
              placeholderTextColor="#5C6B68"
              value={typedName}
              onChangeText={(text) => {
                // autoCapitalize only affects virtual keyboards, so capitalise here too or the
                // field stays lowercase on desktop. Only the first letter of each word is
                // touched, leaving "McDonald" and "O'Brien" as typed.
                setTypedName(text.replace(/(^|[\s'-])([a-z])/g, (m, sep, ch) => sep + ch.toUpperCase()));
                onFormChange?.();
              }}
              autoCapitalize="words"
            />
          </View>
        )}

        {template.finalSignatureSection.requiresHomeAddress && (
          <View style={styles.nameInputContainer}>
            <Text style={styles.nameInputLabel}>
              Home Address <Text style={styles.requiredAsterisk}>*</Text>
            </Text>
            <GoogleAddressInput
              value={homeAddress}
              onChangeText={(text) => {
                setHomeAddress(text);
                onFormChange?.();
              }}
              placeholder="Enter your address (minimum 7 characters)..."
              multiline
              isValidated={homeAddress.trim().length >= 7 ? undefined : false}
              onAddressSelected={(address) => {
                setHomeAddress(address);
                onFormChange?.();
              }}
            />
          </View>
        )}

        {template.finalSignatureSection.requiresSignature && (
          <View style={styles.signatureBox}>
            <Text style={[styles.signatureLabel, styles.fullSignatureLabel]}>
              Full Signature: <Text style={styles.requiredAsterisk}>*</Text>
            </Text>
            {isPhone ? (
              <TouchableOpacity
                dataSet={{ hover: "lift" }}
                style={[styles.signTapButton, !fullSignature && styles.signTapButtonRequired]}
                onPress={() => setShowSignatureModal(true)}
              >
                {fullSignature ? (
                  <Image
                    source={{ uri: fullSignature }}
                    style={styles.signaturePreview}
                    resizeMode="contain"
                  />
                ) : (
                  <Text style={styles.signTapText}>✍️  Tap to sign</Text>
                )}
              </TouchableOpacity>
            ) : (
              <SignaturePad
                fullWidth
                height={360}
                isEmpty={!fullSignature}
                onSignatureChange={(sig) => {
                  setFullSignature(sig);
                  onFormChange?.();
                }}
              />
            )}
            {isPhone && fullSignature && (
              <Text style={styles.signRetakeHint}>Tap your signature to redo it</Text>
            )}
          </View>
        )}
      </View>

      <View style={styles.agreementRow}>
        <TouchableOpacity
          style={[
            styles.checkbox,
            agreed ? styles.checkboxChecked : styles.checkboxUnchecked
          ]}
          onPress={() => setAgreed(!agreed)}
        >
          {agreed && <Text style={styles.checkmark}>✓</Text>}
        </TouchableOpacity>
        <Text style={styles.agreementText}>
          I have read and understand this waiver. I understand that by signing
          this waiver, I am giving up legal rights and remedies that may be
          available to me. I voluntarily agree to be bound by all terms and
          conditions.
        </Text>
      </View>

      <View style={styles.buttonContainer}>
        {/* Cancel Button - Always active */}
        <TouchableOpacity dataSet={{ hover: "lift" }} 
          style={styles.cancelButton} 
          onPress={onCancel}
          disabled={generating || savingWaiver}
        >
          <Text style={styles.cancelButtonText}>
            Cancel
          </Text>
        </TouchableOpacity>
        
        {/* Save Waiver Button - Enabled if (coming from ViewBooking OR payments exist) AND form is valid AND not already saved */}
        <TouchableOpacity dataSet={{ hover: "lift" }}
          style={[
            styles.saveWaiverButton,
            ((!isOriginalBooker && !hasExistingPayment) || !canSign() || generating || savingWaiver || waiverSaved) && styles.saveWaiverButtonDisabled,
          ]}
          onPress={handleSaveWaiver}
          disabled={((!isOriginalBooker && !hasExistingPayment) || !canSign() || generating || savingWaiver || waiverSaved)}
        >
          {savingWaiver ? (
            <ActivityIndicator size="small" color="#2E3A38" />
          ) : (
            <Text style={styles.saveWaiverButtonText}>💾 Save Waiver</Text>
          )}
        </TouchableOpacity>

        {/* Payment Button - Always shown, enabled only if no payments AND form is valid */}
        <TouchableOpacity dataSet={{ hover: "lift" }}
          style={[
            styles.signButton,
            !(hasExistingPayment || !canSign() || generating || savingWaiver || !paymentUrl) && styles.signButtonEnabled,
            (hasExistingPayment || !canSign() || generating || savingWaiver || !paymentUrl) && styles.signButtonDisabled,
          ]}
          onPress={handleSignWaiver}
          disabled={hasExistingPayment || !canSign() || generating || savingWaiver || !paymentUrl}
        >
          {generating ? (
            <ActivityIndicator size="small" color="#FFFFFF" />
          ) : (
            <Text style={styles.signButtonText}>
              {hasExistingPayment ? "✅ Fully Paid!" : "Continue to Payment"}
            </Text>
          )}
        </TouchableOpacity>
      </View>

      {/* Loading Modal for Save Waiver */}
      <Modal
        visible={savingWaiver}
        transparent={true}
        animationType="fade"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <ActivityIndicator size="large" color="#0E7C6B" />
            <Text style={styles.modalText}>Saving waiver...</Text>
          </View>
        </View>
      </Modal>

      {/* Loading Modal for Continue to Payment */}
      <Modal
        visible={generating}
        transparent={true}
        animationType="fade"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <ActivityIndicator size="large" color="#0E7C6B" />
            <Text style={styles.modalText}>Saving waiver and processing payment...</Text>
            <Text style={styles.modalSubtext}>Please wait</Text>
          </View>
        </View>
      </Modal>

      {/* Success Modal for Waiver Saved */}
      <Modal
        visible={showSuccessModal}
        transparent={true}
        animationType="fade"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.successIcon}>✅</Text>
            <Text style={styles.modalText}>Waiver saved successfully!</Text>
            <Text style={styles.modalSubtext}>Your waiver has been saved to your booking.</Text>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.modalButton}
              onPress={() => setShowSuccessModal(false)}
            >
              <Text style={styles.modalButtonText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Full-screen landscape capture, phones only. Kept outside the inline pad
          branch so it survives re-renders while open. */}
      <SignatureCaptureModal
        visible={showSignatureModal}
        title="Sign your name"
        onCancel={() => setShowSignatureModal(false)}
        onConfirm={(sig) => {
          setShowSignatureModal(false);
          setFullSignature(sig);
          onFormChange?.();
        }}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    padding: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
  },
  loadingText: {
    marginTop: 15,
    fontSize: 16,
    color: '#5C6B68',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    padding: 20,
  },
  errorText: {
    fontSize: 18,
    color: '#C4453D',
    marginBottom: 20,
    textAlign: 'center',
  },
  title: {
    fontSize: 22,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 8,
    color: "#0F1A19",
  },
  subtitle: {
    fontSize: 16,
    textAlign: "center",
    marginBottom: 20,
    color: "#5C6B68",
  },
  section: {
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "#FFFFFF",
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#E3F2EF",
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#DDE3E2",
  },
  sectionNumber: {
    fontSize: 12,
    fontWeight: "700",
    color: "#FFFFFF",
    backgroundColor: "#0E7C6B",
    width: 22,
    height: 22,
    borderRadius: 11,
    textAlign: "center",
    lineHeight: 22,
    marginRight: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 12,
    color: "#0F1A19",
  },
  text: {
    fontSize: 14,
    lineHeight: 20,
    color: "#2E3A38",
    marginBottom: 12,
  },
  signatureBox: {
    marginTop: 16,
    marginBottom: 8,
    zIndex: 1,
    position: "relative",
  },
  signatureLabel: {
    fontSize: 12,
    fontWeight: "600",
    marginBottom: 8,
    color: "#5C6B68",
  },
  fullSignatureLabel: {
    textAlign: "center",
    width: "100%",
  },
  requiredAsterisk: {
    color: "#C4453D",
    fontSize: 12,
  },
  instructionText: {
    fontSize: 11,
    color: "#8A9794",
    marginTop: 8,
    textAlign: "center",
    fontStyle: "italic",
  },
  checkbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderRadius: 5,
    backgroundColor: "#FFFFFF",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 12,
  },
  checkboxUnchecked: {
    borderColor: "#C4453D",
    backgroundColor: "#FBEAE9",
  },
  checkboxChecked: {
    borderColor: "#1F8A5B",
    backgroundColor: "transparent",
  },
  checkmark: {
    color: "#1F8A5B",
    fontSize: 14,
    fontWeight: "bold",
  },
  // A real row: the text used to be pulled up beside the checkbox with a negative
  // margin, which clipped once the shared `section` style gained overflow:hidden.
  agreementRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    padding: 16,
  },
  signTapButton: {
    borderWidth: 2,
    borderColor: "#DDE3E2",
    borderStyle: "dashed",
    borderRadius: 8,
    minHeight: 120,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
    cursor: "pointer",
  },
  signTapButtonRequired: { borderColor: "#C4453D", backgroundColor: "#FBEAE9" },
  signTapText: { fontSize: 16, fontWeight: "600", color: "#0E7C6B" },
  signaturePreview: { width: "100%", height: 120 },
  signRetakeHint: { fontSize: 12, color: "#8A9794", marginTop: 6, textAlign: "center" },
  agreementText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    color: "#2E3A38",
    marginLeft: 12,
  },
  buttonContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginTop: 30,
    marginBottom: 20,
    gap: 10,
  },
  cancelButton: {
    flex: 1,
    minWidth: 120,
    paddingVertical: 14,
    paddingHorizontal: 20,
    backgroundColor: "#DDE3E2",
    borderRadius: 6,
    alignItems: "center",
  },
  cancelButtonText: {
    color: "#2E3A38",
    fontSize: 16,
    fontWeight: "600",
  },
  finishButton: {
    backgroundColor: "#1F8A5B",
  },
  finishButtonText: {
    color: "#FFFFFF",
  },
  saveWaiverButton: {
    flex: 1,
    minWidth: 120,
    paddingVertical: 14,
    paddingHorizontal: 20,
    backgroundColor: "#B4761B",
    borderRadius: 6,
    alignItems: "center",
  },
  saveWaiverButtonDisabled: {
    backgroundColor: "#DDE3E2",
    opacity: 0.6,
  },
  saveWaiverButtonText: {
    color: "#2E3A38",
    fontSize: 16,
    fontWeight: "700",
  },
  signButton: {
    flex: 1,
    minWidth: 120,
    paddingVertical: 14,
    paddingHorizontal: 20,
    backgroundColor: "#0E7C6B",
    borderRadius: 6,
    alignItems: "center",
  },
  signButtonEnabled: {
    backgroundColor: "#1F8A5B",
  },
  signButtonDisabled: {
    backgroundColor: "#DDE3E2",
    opacity: 0.6,
  },
  signButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: 24,
    alignItems: "center",
    minWidth: 200,
  },
  modalText: {
    fontSize: 16,
    color: "#5C6B68",
    marginTop: 12,
    textAlign: "center",
  },
  modalSubtext: {
    fontSize: 14,
    color: "#8A9794",
    marginTop: 8,
    textAlign: "center",
  },
  nameInputContainer: {
    marginBottom: 16,
    marginTop: 12,
    zIndex: 1000,
    position: "relative",
  },
  nameInputLabel: {
    fontSize: 12,
    fontWeight: "600",
    marginBottom: 8,
    color: "#5C6B68",
  },
  nameInput: {
    height: 44,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
    backgroundColor: "#FFFFFF",
    width: "100%",
  },
  nameInputRequired: {
    backgroundColor: "#FBEAE9",
  },
  addressHelperText: {
    fontSize: 11,
    color: "#B4761B",
    marginTop: 4,
    fontStyle: "italic",
  },
  successIcon: {
    fontSize: 48,
    textAlign: "center",
    marginBottom: 16,
  },
  modalButton: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    marginTop: 20,
    minWidth: 100,
  },
  modalButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
});

export default RentalWaiver;
